import {
  ObjectSchema,
  Maybe,
  AnyObject,
  AnySchema,
  isSchema,
  object,
} from "yup";
import { BasicStep, Conditional, Field, Step, StepData, Template } from "../../../types";
import {
  FormikErrors,
  FormikHelpers,
  FormikTouched,
  FormikValues,
} from "formik";
import { isEqual } from "lodash";
import { AppDispatch, RootState } from "../../../app/store";
import { isComponentStep } from "../../../utils";

// Flatten step fields validation schema
// Generate nested Yup schema
export const generateYupSchema = (
  fields: Field[] | undefined,
  state?: RootState,
): ObjectSchema<Maybe<AnyObject>> => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const schemaFields: { [key: string]: any } = {};

  fields?.forEach(({ name, validation, conditionalValidation }) => {
    const path = name.split(".");
    let currentLevel = schemaFields;

    path.forEach((key, index) => {
      if (!currentLevel[key]) {
        if (index === path.length - 1) {
          if (conditionalValidation && state)
            currentLevel[key] = conditionalValidation(state);
          else currentLevel[key] = validation;
        } else {
          currentLevel[key] = {};
        }
      }
      currentLevel = currentLevel[key];
    });
  });

  const buildYupObject = (obj: {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    [key: string]: any;
  }): { [key: string]: AnySchema } => {
    const yupShape: { [key: string]: AnySchema } = {};
    for (const key in obj) {
      if (isSchema(obj[key])) {
        yupShape[key] = obj[key] as AnySchema;
      } else {
        yupShape[key] = object().shape(buildYupObject(obj[key]));
      }
    }
    return yupShape;
  };

  return object().shape(buildYupObject(schemaFields));
};

const touchedFromError = (error: unknown): unknown => {
  if (Array.isArray(error)) return error.map(touchedFromError);

  if (error && typeof error === "object") {
    return Object.fromEntries(
      Object.entries(error).map(([key, value]) => [
        key,
        touchedFromError(value),
      ]),
    );
  }

  return true;
};

export const getTouchedFromErrors = (
  errors: FormikErrors<FormikValues>,
): FormikTouched<FormikValues> =>
  touchedFromError(errors) as FormikTouched<FormikValues>;

export const isEmptyObject = (obj: object): boolean => {
  return Object.keys(obj).length === 0;
};

export const getRequiredFieldNames = (
  fields: Field[],
  state: RootState,
  values: FormikValues,
  dispatch: AppDispatch
) =>
  fields
    .filter(
      ({
        validation,
        conditionalValidation,
        conditional,
        allConditionalsTrue,
        conditionalBool
      }) => {
        const requiredSchema = conditionalValidation
          ? isSchemaRequired(conditionalValidation(state))
          : isSchemaRequired(validation);

        if (conditionalBool !== undefined) {
          return requiredSchema && conditionalBool(values, dispatch, state);
        }

        return (
          requiredSchema &&
          (!conditional ||
            getIsConditionallyTrue(values, conditional, allConditionalsTrue))
        );
      }
    )
    .map(({ name }) => name);

export const getOptionalFieldNames = (
  fields: Field[],
  state: RootState,
  values: FormikValues,
  dispatch: AppDispatch
) =>
  fields
    .filter(
      ({
        validation,
        conditionalValidation,
        conditional,
        allConditionalsTrue,
        conditionalBool
      }) => {
        const notRequiredSchema = conditionalValidation
          ? !isSchemaRequired(conditionalValidation(state))
          : !isSchemaRequired(validation);
        
        if (conditionalBool !== undefined) {
          return (
            notRequiredSchema && conditionalBool(values, dispatch, state)
          );
        }

        return (
          notRequiredSchema &&
          (!conditional ||
            getIsConditionallyTrue(values, conditional, allConditionalsTrue))
        );
      }
    )
    .map(({ name }) => name);

export const isSchemaRequired = (schema?: AnySchema): boolean => {
  return !!(
    (
      schema &&
      (schema.describe().tests.some((test) => test.name === "required") ||
        !schema.describe().optional)
    ) //When schema is 'mixed' the test 'required' doesn't exist
  );
};
// Get all keys from an object
export const getKeys = (
  obj: Record<string, unknown>,
  prefix = ""
): string[] => {
  return Object.entries(obj).reduce((res: string[], [key, value]) => {
    const newKey = prefix ? `${prefix}.${key}` : key;
    if (typeof value === "object" && value !== null && !Array.isArray(value)) {
      res = res.concat(getKeys(value as Record<string, unknown>, newKey));
    } else {
      res.push(newKey);
    }
    return res;
  }, []);
};

// Get value even if name is nested
export const getValue = (values: FormikValues, name: string) => {
  const value = name.split(".").reduce((acc, part) => acc && acc[part], values);

  // An empty array is a truthy value, so we return an empty string instead
  if (Array.isArray(value) && value.length === 0) {
    return "";
  }

  return value;
};

export const currentIsSubstep = (currentStep: Step | undefined) => {
  return !!currentStep?.subSteps;
};

export const isStepSkipped = (
  stepData: Step | undefined,
  values: FormikValues | undefined,
  state: RootState
) => {
  if (!values || !stepData) return false;
  if (stepData.skipWhen?.(values, state)) return true;
  return (
    !!stepData.subSteps?.length &&
    stepData.subSteps.every((subStepData) =>
      subStepData.skipWhen?.(values, state)
    )
  );
};

export const getNextNavigableStep = (
  steps: Step[] | null | undefined,
  candidate: number,
  values: FormikValues | undefined,
  state: RootState,
  lastStep: number
): number => {
  if (!values) return candidate;
  let step = candidate;
  while (
    step <= lastStep &&
    isStepSkipped(
      steps?.find((s) => s.step === step),
      values,
      state
    )
  ) {
    step += 1;
  }
  return step;
};

export const getNextNavigableSubStep = (
  stepData: Step | undefined,
  candidate: number,
  values: FormikValues | undefined,
  state: RootState
): number | null => {
  const subSteps = stepData?.subSteps;
  if (!values || !subSteps?.length) return null;

  let subStep = candidate;
  while (subStep <= subSteps.length) {
    const subStepData = subSteps.find((s) => Number(s.step) === subStep);
    if (!subStepData?.skipWhen?.(values, state)) return subStep;
    subStep += 1;
  }
  return null;
};

export const isBackNavigationDisabled = (
  disableBackNavigation: Step["disableBackNavigation"],
  values: FormikValues | undefined,
  nextStepStatus: "completed" | "disabled"
) =>
  typeof disableBackNavigation === "function"
    ? disableBackNavigation(values || {}, nextStepStatus)
    : !!disableBackNavigation;

export const getPreviousNavigableSubStep = (
  stepData: Step | undefined,
  candidate: number,
  values: FormikValues | undefined,
  state: RootState
): number | null => {
  const subSteps = stepData?.subSteps;
  if (!values || !subSteps?.length) return null;

  let subStep = candidate;
  while (subStep >= 1) {
    const subStepData = subSteps.find((s) => Number(s.step) === subStep);
    if (subStepData && !subStepData.skipWhen?.(values, state)) return subStep;
    subStep -= 1;
  }
  return null;
};

export const getLastNavigableSubStep = (
  stepData: Step | undefined,
  values: FormikValues | undefined,
  state: RootState
): number | null => {
  const subSteps = stepData?.subSteps;
  if (!subSteps?.length) return null;

  const lastSubStep = Math.max(...subSteps.map(({ step }) => Number(step)));

  return getPreviousNavigableSubStep(stepData, lastSubStep, values, state);
};

export const getResumeStepData = ({
  steps,
  stepData,
  values,
  state,
}: {
  steps: Step[] | null | undefined;
  stepData: StepData;
  values: FormikValues | undefined;
  state: RootState;
}): { step: number; substep: number } => {
  const {
    reachedSteps,
    lastCompletedSubStep,
    step: currentStep,
    lastNumericStep,
    subStep: persistedSubStep,
  } = stepData;

  const persistedStep =
    typeof currentStep === "number" ? currentStep : lastNumericStep;

  const lastStep =
    steps?.filter(({ step }) => typeof step === "number").length ?? 0;

  const step = getNextNavigableStep(
    steps,
    reachedSteps?.[reachedSteps.length - 1] || 1,
    values,
    state,
    lastStep
  );

  const candidate = Math.max(
    lastCompletedSubStep + 1,
    persistedStep === step ? Number(persistedSubStep) || 1 : 1
  );

  const targetStepData = steps?.find((s) => s.step === step);
  const subStep = getNextNavigableSubStep(
    targetStepData,
    candidate,
    values,
    state
  );

  if (targetStepData?.subSteps?.length && subStep === null) {
    const nextStep = getNextNavigableStep(
      steps,
      step + 1,
      values,
      state,
      lastStep
    );
    if (nextStep <= lastStep) {
      return {
        step: nextStep,
        substep:
          getNextNavigableSubStep(
            steps?.find((s) => s.step === nextStep),
            1,
            values,
            state
          ) ?? 1,
      };
    }
  }

  return { step, substep: subStep ?? 1 };
};

// Object is empty
export const isObjectEmpty = (obj: Record<string, unknown>): boolean => {
  return Object.keys(obj).length === 0;
};

export const setNestedValue = (
  obj: FormikValues,
  path: string,
  value: unknown
) => {
  const keys = path.split(".");
  let current = obj;

  for (let i = 0; i < keys.length - 1; i++) {
    const key = keys[i];
    if (!current[key]) {
      current[key] = {};
    }
    current = current[key];
  }

  current[keys[keys.length - 1]] = value;
};

export const mergeDeep = (target: AnyObject, source: AnyObject): AnyObject => {
  const output = { ...target };
  for (const key in source) {
    const sourceValue = source[key];
    const targetValue = target[key];

    // Check if the value is a plain object
    if (sourceValue && isPlainObject(sourceValue)) {
      if (isPlainObject(targetValue)) {
        output[key] = mergeDeep(targetValue, sourceValue);
      } else {
        output[key] = mergeDeep({}, sourceValue);
      }
    } else {
      // Handle non-plain objects like File
      output[key] = sourceValue;
    }
  }
  return output;
};

/** Applies field initialValue only where Formik has no value yet (undefined, null, or ""). */
export const getMissingFieldInitialValues = (
  values: FormikValues,
  fields: Field[],
): FormikValues => {
  const missingDefaults: FormikValues = {};

  fields.forEach(({ name, initialValue }) => {
    const current = getValue(values, name);
    const isEmpty =
      current === undefined || current === null || current === "";

    if (!isEmpty || initialValue === "") return;

    setNestedValue(missingDefaults, name, initialValue);
  });

  return missingDefaults;
};

// Helper to check for plain objects (ignoring instances like File, Blob, etc.)
const isPlainObject = (value: unknown): boolean => {
  return (
    typeof value === "object" &&
    value !== null &&
    Object.getPrototypeOf(value) === Object.prototype
  );
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const isObject = (item: any): item is AnyObject => {
  return item && typeof item === "object" && !Array.isArray(item);
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const isArray = (item: any): item is Array<any> =>
  item && Array.isArray(item);

// Reorder array to have defined elements first and undefined elements last
const reorderArray = (arr: Array<unknown>) => {
  // Filter out nullish elements
  const definedElements = arr.filter(
    (element) => element !== null && element !== undefined
  );

  // Determine the number of undefined elements to add back
  const nullCount = arr.length - definedElements.length;

  // Create a new array with the defined elements followed by the undefined elements
  const result = [...definedElements, ...Array(nullCount).fill(undefined)];

  return result;
};

// Check if an array needs reordering
const needsReorder = (arr: Array<unknown>) => {
  // Create a reordered version of the array
  const reorderedArray = reorderArray(arr);

  // Check if the input array is the same as the reordered array using deep equality
  for (let i = 0; i < arr.length; i++) {
    if (!isEqual(arr[i], reorderedArray[i])) {
      return true; // The array needs reordering
    }
  }
  return false; // The array does not need reordering
};

// Used to reorder fields that are grouped together to be displayed without empty spaces
export const reorderFields = (
  currentStepData: BasicStep,
  values: FormikValues,
  setValues: FormikHelpers<FormikValues>["setValues"]
) => {
  const fields = currentStepData?.fields;
  const haveReorderGroups = fields?.some((field) => field.groupReorder);

  // If the current step has reorder groups
  if (fields && haveReorderGroups) {
    // Groups of fields with same groupReorder name
    const reorderGroups = fields
      .filter((field) => field.groupReorder)
      .reduce((acc, field) => {
        const reorderFieldName = field.groupReorder as string;
        if (!acc.includes(reorderFieldName)) {
          acc.push(reorderFieldName);
        }
        return acc;
      }, [] as string[]);

    // Iterate each group
    reorderGroups.forEach((reorderFieldName) => {
      // Fields that belong to the current group
      const reorderGroupFields = fields.filter(
        (field) => field.groupReorder === reorderFieldName
      );

      // Values of the fields that belong to the current group
      const reorderGroupValues = reorderGroupFields.map((field) =>
        getValue(values, field.name)
      );

      // Check if the group needs reordering
      if (needsReorder(reorderGroupValues)) {
        // Reordered values
        const reorderedGroupValues = reorderArray(reorderGroupValues);

        // Create an object with the reordered values
        // Having in account the nested keys
        const reorderedValues = reorderGroupFields.reduce(
          (acc, field, index) => {
            setNestedValue(acc, field.name, reorderedGroupValues[index]);

            return acc;
          },
          {}
        );

        // Merge the reordered values with the current formik values
        const mergedValues = mergeDeep(values, reorderedValues);

        // Set the values to formik
        setValues(mergedValues);
      }
    });
  }
};

export const getNestedValue = (
  obj: Record<string, unknown>,
  name: string | string[]
): unknown => {
  let path;

  if (Array.isArray(name)) {
    path = name;
  } else {
    path = name ? name?.split(".") : [];
  }

  if (path.length === 0) return obj;
  const [head, ...tail] = path;
  if (typeof obj[head] === "object" && obj[head] !== null) {
    return getNestedValue(obj[head] as Record<string, unknown>, tail.join("."));
  }
  return obj[head];
};

export const getIsConditionallyTrue = (
  values: FormikValues,
  conditional: Conditional | Conditional[],
  allConditionalsTrue?: boolean
) => {
  const checkConditional = (cond: Conditional) => {
    const conditionalValue = getNestedValue(values, cond.name) as string;
    return Array.isArray(cond.value)
      ? cond.value.includes(conditionalValue)
      : cond.value === conditionalValue;
  };

  if (Array.isArray(conditional)) {
    return allConditionalsTrue
      ? conditional.every(checkConditional)
      : conditional.some(checkConditional);
    // If the conditional field is shown by a single field
  } else {
    // If the conditional field has the correct value return true
    if (conditional.name) {
      const conditionalValue = getNestedValue(
        values,
        conditional.name
      ) as string;

      return Array.isArray(conditional.value)
        ? conditional.value.includes(conditionalValue)
        : conditional.value === conditionalValue;
    }
  }
};

export const getShouldFieldRender = (
  field: Field,
  values: FormikValues,
  dispatch: AppDispatch,
  state: RootState,
): boolean => {
  const {
    hideField,
    conditionalFile,
    conditionalBool,
    conditional,
    allConditionalsTrue,
    name,
  } = field;

  if (hideField) return false;

  if (conditionalFile) {
    return (
      !!getNestedValue(values, conditionalFile.name) ||
      !!getNestedValue(values, name)
    );
  }

  if (conditionalBool) return conditionalBool(values, dispatch, state);

  if (conditional) {
    return !!getIsConditionallyTrue(values, conditional, allConditionalsTrue);
  }

  return true;
};

export const groupFieldsIntoRows = (
  fields: Field[],
): { columns: 1 | 2 | 3; fields: Field[] }[] => {
  const normalize = (columns?: number): 1 | 2 | 3 =>
    columns === 2 ? 2 : columns === 3 ? 3 : 1;

  const rows: { columns: 1 | 2 | 3; fields: Field[] }[] = [];
  let index = 0;

  while (index < fields.length) {
    const size = normalize(fields[index].columns);
    const group: Field[] = [];

    while (
      index < fields.length &&
      group.length < size &&
      normalize(fields[index].columns) === size &&
      !(group.length > 0 && fields[index].startsRow)
    ) {
      group.push(fields[index]);
      index += 1;
    }

    rows.push({ columns: size, fields: group });
  }

  return rows;
};

export const scrollFormToTop = (node: HTMLElement | null) => {
  let parent = node?.parentElement;

  while (parent) {
    const { overflowY } = getComputedStyle(parent);

    if (overflowY === "auto" || overflowY === "scroll") {
      parent.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }

    parent = parent.parentElement;
  }

  window.scrollTo({ top: 0, behavior: "smooth" });
};

export const getRemoteStepData = ({
  template,
  state,
  values,
  dispatch,
}: {
  template?: Template | null;
  state: RootState;
  values: FormikValues;
  dispatch: AppDispatch;
}): { step: number; substep: number } | null => {
  if (!template?.steps) return null;

  let step: Step["step"] | null = null;
  let substep: Step["step"] | null = null;

  const getEmptyRequiredField = (
    fields: Field[],
    targetValues: FormikValues = values,
  ) => {
    const requiredFields = getRequiredFieldNames(
      fields,
      state,
      targetValues,
      dispatch,
    );
    return requiredFields.some((field) => !getValue(targetValues, field));
  };

  template.steps.find(
    ({ step: currentStep, fields, subSteps, secondaryForm, skipWhen }) => {
      // Step is not applicable for the current values (skipped imperatively in
      // onContinue) → treat as complete so the resume point moves past it.
      if (skipWhen?.(values, state)) return false;
      if (fields) {
        if (getEmptyRequiredField(fields)) {
          step = currentStep;
          return true;
        }
      } else if (subSteps) {
        const foundSubStep = subSteps.find((subStep) => {
          if (isComponentStep(subStep)) return false;
          if (subStep.skipWhen?.(values, state)) return false;

          return getEmptyRequiredField(subStep.fields || []);
        });
        if (foundSubStep && !isComponentStep(foundSubStep)) {
          step = currentStep;
          substep = foundSubStep.step;
          return true;
        }
      } else if (secondaryForm) {
        const entities: FormikValues[] =
          values[secondaryForm.collectionName] || [];
        if (
          !entities.length ||
          entities.some((entity) =>
            getEmptyRequiredField(secondaryForm.fields, entity),
          )
        ) {
          step = currentStep;
          return true;
        }
      }
    },
  );

  if (step !== null)
    return { step: Number(step), substep: Number(substep) || 1 };

  // Every step is complete → resume on the last applicable step and its last
  // applicable subStep, instead of falling back to the beginning of the flow.
  const applicableSteps = template.steps.filter(
    (templateStep) =>
      typeof templateStep.step === "number" &&
      !templateStep.skipWhen?.(values, state),
  );
  const lastStep = applicableSteps[applicableSteps.length - 1];

  if (!lastStep) return null;

  const applicableSubSteps =
    lastStep.subSteps?.filter(
      (subStep) =>
        !isComponentStep(subStep) && !subStep.skipWhen?.(values, state),
    ) || [];
  const lastSubStep = applicableSubSteps[applicableSubSteps.length - 1];

  return {
    step: Number(lastStep.step),
    substep: Number(lastSubStep?.step) || 1,
  };
};
